/** CIS monthly return deadline. HMRC tax months end on the 5th and the return is due by the 19th of that month. */

import { format } from 'date-fns';
import type { CISReturnStatus } from '../../types/accounting';

const DUE_DAY = 19;

/** Due date for the return whose tax month is `taxMonth` (YYYY-MM-DD, any day in the month). Null when unparseable. */
export function cisReturnDueDate(taxMonth: string): Date | null {
  const match = /^(\d{4})-(\d{2})/.exec(taxMonth);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(Number(match[1]), month - 1, DUE_DAY);
}

/** Midnight local time on the calendar date it currently is in London, so the deadline flips at UK midnight wherever the browser is. */
function ukCalendarDay(now: Date): Date {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return new Date(get('year'), get('month') - 1, get('day'));
}

export interface CISDueCue {
  label: string;
  overdue: boolean;
}

/** Only draft returns are still owed; a submitted or amended return has met its deadline. */
export function cisDueCue(taxMonth: string, status: CISReturnStatus, now: Date): CISDueCue | null {
  if (status !== 'draft') return null;
  const due = cisReturnDueDate(taxMonth);
  if (!due) return null;
  return { label: `Due ${format(due, 'd MMM yyyy')}`, overdue: ukCalendarDay(now) > due };
}

/** "March 2026" for a tax month; the raw text back when it is not a date. */
export function formatCisTaxMonth(taxMonth: string): string {
  try {
    return format(new Date(taxMonth), 'MMMM yyyy');
  } catch {
    return taxMonth;
  }
}
